import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, BookOpen, Sailboat, Check, Trophy, HeartHandshake, Share2, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CARD_MILESTONES, MilestoneCardDialog } from "./MilestoneCard";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { api, errMsg } from "@/lib/api";

export const refreshStreak = () => api.get("/streak").then((r) => announceStreak(r.data)).catch(() => {});

export const STREAK_EVENT = "cast-streak";
export const announceStreak = (s: any) => window.dispatchEvent(new CustomEvent(STREAK_EVENT, { detail: s }));

export function useStreak() {
  const [s, setS] = useState<any>(null);
  useEffect(() => {
    api.get("/streak").then((r) => setS(r.data)).catch(() => {});
    const h = (e: any) => setS(e.detail);
    window.addEventListener(STREAK_EVENT, h);
    return () => window.removeEventListener(STREAK_EVENT, h);
  }, []);
  return s;
}

export function StreakChip() {
  const s = useStreak();
  if (!s) return null;
  return (
    <Link to="/shore" data-testid="streak-chip" title={s.today_done ? "You've shown up today" : "Read today's verse to keep your streak"}
      className={`flex items-center gap-1 rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${s.today_done ? "bg-amber-100 text-amber-800" : "bg-white text-slate-500 ring-1 ring-[#E2DBCE] hover:bg-[#F3ECE0]"}`}>
      <Flame className={`h-4 w-4 ${s.today_done ? "fill-amber-400 text-amber-500" : ""}`} /><span data-testid="streak-chip-count">{s.current}</span>
    </Link>
  );
}

const Step = ({ done, icon: Icon, title, children, testId }: any) => (
  <div className={`flex items-center gap-4 rounded-2xl p-4 ${done ? "bg-[#5B7065]/10" : "bg-white ring-1 ring-[#E2DBCE]"}`} data-testid={testId} data-done={done}>
    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${done ? "bg-[#5B7065] text-white" : "bg-[#F3ECE0] text-[#C86D51]"}`}>{done ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}</span>
    <div className="flex-1"><p className={`font-semibold ${done ? "text-[#3f5248]" : "text-slate-900"}`}>{title}</p>{children}</div>
  </div>
);

export function StreakCard({ verse, cont }: { verse: any; cont: any }) {
  const s = useStreak();
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [cardM, setCardM] = useState<number | null>(null);
  useEffect(() => {
    if (!s?.today_done || !CARD_MILESTONES[s.current]) return;
    const key = `cast_card_${user.id}_${s.current}`;
    if (!localStorage.getItem(key)) { localStorage.setItem(key, "1"); setCardM(s.current); }
  }, [s, user.id]);
  if (!s) return null;
  const read = s.today_kinds.includes("verse");
  const storied = s.today_kinds.some((k: string) => ["story", "devotion", "study"].includes(k));
  const markRead = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/streak/verse");
      announceStreak(data);
      if (!s.today_done) confetti({ particleCount: data.milestones_reached.includes(data.current) ? 160 : 60, spread: 75, origin: { y: 0.7 }, colors: ["#C86D51", "#F5C98B", "#5B7065", "#1E293B"], disableForReducedMotion: true });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const prevMilestone = [0, ...s.milestones_reached].filter((m: number) => m <= s.current).pop() || 0;
  const pct = s.next_milestone ? ((s.current - prevMilestone) / (s.next_milestone - prevMilestone)) * 100 : 100;
  const msg = s.today_done ? (s.current > 1 ? `${s.current} days in a row. His mercies are new every morning.` : "You've shown up today. Come back tomorrow to start a streak.")
    : s.current > 0 ? `Keep your ${s.current}-day streak going. One small step today.` : "Start a streak today with one verse.";
  const lastGrace = s.grace_days[s.grace_days.length - 1];
  const graceDay = lastGrace && new Date(`${lastGrace}T12:00:00`).toLocaleDateString(undefined, { weekday: "long" });
  const graceMsg = !s.grace_available && lastGrace ? `Grace day used ${graceDay}. Your streak was kept. A new grace day arrives Sunday.`
    : "1 grace day this week. If life gets busy and you miss a day, your streak stays safe.";

  return (
    <section className="card-warm grid gap-8 p-6 sm:p-8 lg:grid-cols-12" data-testid="streak-card">
      <div className="lg:col-span-5">
        <p className="eyebrow">Daily tracker</p>
        <div className="mt-4 flex items-center gap-4">
          <motion.div key={s.current} initial={{ scale: 0.6 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 300, damping: 12 }}
            className={`flex h-20 w-20 items-center justify-center rounded-full ${s.today_done ? "bg-gradient-to-br from-amber-300 to-[#C86D51] shadow-lg shadow-amber-500/30" : "bg-[#F3ECE0]"}`}>
            <Flame className={`h-10 w-10 ${s.today_done ? "fill-white text-white" : "text-[#C86D51]"}`} />
          </motion.div>
          <div>
            <p className="font-display text-5xl font-semibold text-slate-900" data-testid="streak-current">{s.current}<span className="ml-2 text-lg text-slate-500">day{s.current === 1 ? "" : "s"}</span></p>
            <p className="text-sm text-slate-500" data-testid="streak-best">Best: {s.best} · {s.total_days} day{s.total_days === 1 ? "" : "s"} in all</p>
          </div>
        </div>
        <p className="mt-4 text-slate-700" data-testid="streak-message">{msg}</p>
        <p className="mt-2 flex items-start gap-1.5 text-sm text-[#3f5248]" data-testid="streak-grace-status">
          <HeartHandshake className="mt-0.5 h-4 w-4 shrink-0" />
          {graceMsg}
        </p>
        <div className="mt-5 flex justify-between" data-testid="streak-week">
          {s.week.map((d: any) => (
            <div key={d.date} className="flex flex-col items-center gap-1.5">
              <span title={d.grace ? "Grace day: your streak was kept" : undefined} data-testid={d.grace ? "streak-grace-dot" : undefined}
                className={`flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold transition-colors ${d.done ? "bg-[#C86D51] text-white" : d.grace ? "bg-[#5B7065]/15 text-[#5B7065] ring-2 ring-dashed ring-[#5B7065]/50" : d.today ? "ring-2 ring-[#C86D51] text-[#C86D51]" : d.future ? "bg-[#F3ECE0]/50 text-slate-300" : "bg-[#F3ECE0] text-slate-400"}`}>
                {d.done ? <Flame className="h-4 w-4 fill-white" /> : d.grace ? <HeartHandshake className="h-4 w-4" /> : d.label}
              </span>
            </div>
          ))}
        </div>
        {s.next_milestone && (
          <div className="mt-5">
            <div className="flex items-center justify-between text-xs font-semibold text-slate-500"><span className="flex items-center gap-1"><Trophy className="h-3.5 w-3.5 text-amber-500" />Next milestone</span><span data-testid="streak-next-milestone">{s.next_milestone} days</span></div>
            <Progress value={pct} className="mt-2 h-1.5" />
          </div>
        )}
      </div>
      <div className="space-y-3 lg:col-span-7">
        <p className="text-sm font-semibold text-slate-500">Today's steps, any one keeps your streak</p>
        <Step done={read} icon={BookOpen} title="Read today's verse" testId="streak-step-verse">
          <p className="mt-0.5 font-scripture text-lg italic text-slate-700">“{verse.text}” <span className="not-italic text-xs font-semibold text-amber-800">{verse.ref}</span></p>
          {!read && <button data-testid="streak-mark-verse-button" disabled={busy} onClick={markRead} className="mt-2 rounded-full bg-[#C86D51] px-4 py-1.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60">I've read it</button>}
        </Step>
        <Step done={storied} icon={Sailboat} title={cont ? `Continue: ${cont.title}` : "Read a story, lesson or devotion"} testId="streak-step-story">
          <Link to={cont ? `/episode/${cont.id}` : "/boat"} data-testid="streak-continue-link" className="mt-1 inline-block text-sm font-semibold text-[#C86D51]">{storied ? "Done today. Keep reading →" : cont ? "Pick up where you left off →" : "Choose a story →"}</Link>
        </Step>
        {s.milestones_reached.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1" data-testid="streak-milestones">
            {s.milestones_reached.map((m: number) => <span key={m} className="flex items-center gap-1 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800"><Trophy className="h-3 w-3" />{m}-day</span>)}
          </div>
        )}
        <div className="rounded-2xl bg-[#F3ECE0]/60 p-4" data-testid="milestone-cards">
          <p className="text-sm font-semibold text-slate-700">Milestone cards</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.keys(CARD_MILESTONES).map(Number).map((m) => {
              const got = s.best >= m;
              return got ? (
                <button key={m} data-testid={`milestone-card-open-${m}`} onClick={() => setCardM(m)}
                  className="flex items-center gap-1.5 rounded-full bg-[#1E293B] px-3.5 py-1.5 text-xs font-semibold text-white transition-transform hover:-translate-y-0.5"><Share2 className="h-3.5 w-3.5" />Share {m}-day card</button>
              ) : (
                <span key={m} data-testid={`milestone-card-locked-${m}`} className="flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-xs font-semibold text-slate-400 ring-1 ring-[#E2DBCE]"><Lock className="h-3 w-3" />{m} days · {m - s.current > 0 ? `${m - s.current} to go` : "keep going"}</span>
              );
            })}
          </div>
        </div>
      </div>
      {cardM && <MilestoneCardDialog milestone={cardM} username={user.username} open={!!cardM} onOpenChange={(o) => !o && setCardM(null)} />}
    </section>
  );
}
